import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  Navigate,
} from '@tanstack/react-router'
import AppLayout from '@/components/layout/AppLayout'
import VolunteerLayout from '@/components/layout/VolunteerLayout'
import PlatformLayout from '@/components/layout/PlatformLayout'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import ListPageSkeleton from '@/components/common/ListPageSkeleton'
import DashboardSkeleton from '@/components/common/DashboardSkeleton'
import DetailPageSkeleton from '@/components/common/DetailPageSkeleton'
import FormPageSkeleton from '@/components/common/FormPageSkeleton'
import DemoTourProvider from '@/features/demo/tour/DemoTourProvider'
// import AssistantProvider from '@/features/assistant/AssistantProvider';
// import AssistantFab from '@/features/assistant/AssistantFab';
// import AssistantDrawer from '@/features/assistant/AssistantDrawer';
import MicroTourProvider from '@/features/assistant/tours/MicroTourProvider'
import { PageTourProvider } from '@/features/pageTours'
import { useAuth } from '@/hooks/useAuth'
import { lazyPage } from './lazyPage'

// Public
const LoginPage = lazyPage(() => import('@/features/auth/LoginPage'))
const RegisterPage = lazyPage(() => import('@/features/auth/RegisterPage'))
const ForgotPasswordPage = lazyPage(
  () => import('@/features/auth/ForgotPasswordPage'),
)
const JoinPage = lazyPage(() => import('@/features/auth/JoinPage'))
const DemoPage = lazyPage(() => import('@/features/demo/DemoPage'))

// Dashboard
const DashboardPage = lazyPage(
  () => import('@/features/dashboard/DashboardPage'),
  <DashboardSkeleton />,
)
const VolunteerDashboard = lazyPage(
  () => import('@/features/dashboard/VolunteerDashboard'),
)

// Members
const MembersListPage = lazyPage(
  () => import('@/features/members/MembersListPage'),
  <ListPageSkeleton rows={8} columns={5} />,
)
const MemberDetailPage = lazyPage(
  () => import('@/features/members/MemberDetailPage'),
  <DetailPageSkeleton />,
)
const MemberFormPage = lazyPage(
  () => import('@/features/members/MemberFormPage'),
  <FormPageSkeleton fields={8} />,
)

// Tasks
const TasksListPage = lazyPage(
  () => import('@/features/tasks/TasksListPage'),
  <ListPageSkeleton rows={8} columns={6} />,
)
const TaskDetailPage = lazyPage(
  () => import('@/features/tasks/TaskDetailPage'),
  <DetailPageSkeleton />,
)
const TaskFormPage = lazyPage(
  () => import('@/features/tasks/TaskFormPage'),
  <FormPageSkeleton fields={7} />,
)
const BatchTaskFormPage = lazyPage(
  () => import('@/features/tasks/BatchTaskFormPage'),
  <FormPageSkeleton fields={5} maxWidth={800} />,
)

// Care Reasons
const CareReasonsListPage = lazyPage(
  () => import('@/features/careReasons/CareReasonsListPage'),
  <ListPageSkeleton rows={8} columns={5} />,
)
const CareReasonDetailPage = lazyPage(
  () => import('@/features/careReasons/CareReasonDetailPage'),
  <DetailPageSkeleton />,
)
const CareReasonFormPage = lazyPage(
  () => import('@/features/careReasons/CareReasonFormPage'),
  <FormPageSkeleton fields={6} />,
)
const CareReasonBatchFormPage = lazyPage(
  () => import('@/features/batches/CareReasonBatchFormPage'),
  <FormPageSkeleton fields={5} />,
)
const BatchDetailPage = lazyPage(
  () => import('@/features/batches/BatchDetailPage'),
  <DetailPageSkeleton />,
)

// Groups
const GroupsListPage = lazyPage(
  () => import('@/features/groups/GroupsListPage'),
  <ListPageSkeleton rows={6} columns={4} />,
)
const GroupDetailPage = lazyPage(
  () => import('@/features/groups/GroupDetailPage'),
  <DetailPageSkeleton />,
)
const GroupFormPage = lazyPage(
  () => import('@/features/groups/GroupFormPage'),
  <FormPageSkeleton fields={4} />,
)

// Goals
const GoalsListPage = lazyPage(
  () => import('@/features/goals/GoalsListPage'),
  <ListPageSkeleton rows={8} columns={5} />,
)
const GoalDetailPage = lazyPage(
  () => import('@/features/goals/GoalDetailPage'),
  <DetailPageSkeleton />,
)
const GoalFormPage = lazyPage(
  () => import('@/features/goals/GoalFormPage'),
  <FormPageSkeleton fields={6} />,
)

// Reports
const ReportsPage = lazyPage(() => import('@/features/reports/ReportsPage'))

// Road Map
const RoadmapPage = lazyPage(() => import('@/features/roadmap/RoadmapPage'))

// Admin
const UsersPage = lazyPage(
  () => import('@/features/admin/UsersPage'),
  <ListPageSkeleton rows={6} columns={5} />,
)
const BillingPage = lazyPage(() => import('@/features/billing/BillingPage'))
const IntegrationsListPage = lazyPage(
  () => import('@/features/integrations/IntegrationsListPage'),
  <ListPageSkeleton rows={6} columns={4} />,
)
const IntegrationFormPage = lazyPage(
  () => import('@/features/integrations/IntegrationFormPage'),
  <FormPageSkeleton fields={6} />,
)

// Settings
const SettingsPage = lazyPage(() => import('@/features/settings/SettingsPage'))
const ProfileSettingsPage = lazyPage(
  () => import('@/features/settings/ProfileSettingsPage'),
)
const PasswordSettingsPage = lazyPage(
  () => import('@/features/settings/PasswordSettingsPage'),
)
const NotificationSettingsPage = lazyPage(
  () => import('@/features/settings/NotificationSettingsPage'),
)
const TemplatesPage = lazyPage(() => import('@/features/templates/TemplatesPage'))

// Platform
const PlatformOverviewPage = lazyPage(
  () => import('@/features/platform/PlatformOverviewPage'),
)
const PlatformChurchesPage = lazyPage(
  () => import('@/features/platform/churches/PlatformChurchesPage'),
)
const PlatformChurchDetailPage = lazyPage(
  () => import('@/features/platform/churches/PlatformChurchDetailPage'),
)
const PlatformUsersPage = lazyPage(
  () => import('@/features/platform/users/PlatformUsersPage'),
)
const PlatformAnalyticsPage = lazyPage(
  () => import('@/features/platform/analytics/PlatformAnalyticsPage'),
)
const PlatformAuditLogsPage = lazyPage(
  () => import('@/features/platform/auditLogs/PlatformAuditLogsPage'),
)

// Root route — just renders child routes
const rootRoute = createRootRoute({
  component: Outlet,
})

// Public routes (login, register)
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
})

const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join',
  component: JoinPage,
  validateSearch: (search: Record<string, unknown>) => ({
    code: (search.code as string) || '',
  }),
})

const demoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/demo',
  component: DemoPage,
})

// Authenticated layout wrapper
function AuthenticatedLayout() {
  const { user, role, loading } = useAuth()

  if (loading) return <LoadingSpinner />
  if (!user) return <Navigate to="/login" />

  if (role === 'volunteer') {
    return (
      <VolunteerLayout>
        <Outlet />
      </VolunteerLayout>
    )
  }

  return (
    <DemoTourProvider>
      <MicroTourProvider>
        <PageTourProvider>
          {/* To re-enable the Flock Assistant, wrap AppLayout with:
                <AssistantProvider>
                  <AppLayout>...</AppLayout>
                  <AssistantFab />
                  <AssistantDrawer />
                </AssistantProvider>
               and uncomment the imports at the top of this file. */}
          <AppLayout>
            <Outlet />
          </AppLayout>
        </PageTourProvider>
      </MicroTourProvider>
    </DemoTourProvider>
  )
}

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  component: AuthenticatedLayout,
})

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/dashboard',
  component: DashboardRouter,
})

const membersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/members',
  component: MembersListPage,
})

const memberNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/members/new',
  component: MemberFormPage,
})

const memberDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/members/$memberId',
  component: MemberDetailPage,
})

const memberEditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/members/$memberId/edit',
  component: MemberFormPage,
})

const tasksRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tasks',
  component: TasksListPage,
})

const taskNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tasks/new',
  component: TaskFormPage,
  validateSearch: (search: Record<string, unknown>) => ({
    memberId: (search.memberId as string) || '',
  }),
})

const taskBatchRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tasks/batch',
  component: BatchTaskFormPage,
})

const taskDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tasks/$taskId',
  component: TaskDetailPage,
})

const taskEditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tasks/$taskId/edit',
  component: TaskFormPage,
})

const careReasonsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/care-reasons',
  component: CareReasonsListPage,
})

const careReasonNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/care-reasons/new',
  component: CareReasonFormPage,
  validateSearch: (search: Record<string, unknown>) => ({
    memberId: (search.memberId as string) || '',
  }),
})

const careReasonDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/care-reasons/$reasonId',
  component: CareReasonDetailPage,
})

const careReasonEditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/care-reasons/$reasonId/edit',
  component: CareReasonFormPage,
})

const groupsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/groups',
  component: GroupsListPage,
})

const groupNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/groups/new',
  component: GroupFormPage,
})

const groupDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/groups/$groupId',
  component: GroupDetailPage,
})

const groupEditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/groups/$groupId/edit',
  component: GroupFormPage,
})

const goalsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/goals',
  component: GoalsListPage,
})

const goalNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/goals/new',
  component: GoalFormPage,
})

const goalDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/goals/$goalId',
  component: GoalDetailPage,
})

const goalEditRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/goals/$goalId/edit',
  component: GoalFormPage,
})

const reportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reports',
  component: ReportsPage,
})

const roadmapRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/roadmap',
  component: RoadmapPage,
})

// Volunteer guard — blocks volunteers from non-volunteer routes
function NonVolunteerLayout() {
  const { role } = useAuth()

  if (role === 'volunteer') return <Navigate to="/dashboard" />

  return <Outlet />
}

// Dashboard router — renders the appropriate dashboard based on role
function DashboardRouter() {
  const { role } = useAuth()

  if (role === 'volunteer') return <VolunteerDashboard />

  return <DashboardPage />
}

// Demo guard — blocks certain routes for demo users
function NoDemoLayout() {
  const { isDemo } = useAuth()

  if (isDemo) return <Navigate to="/dashboard" />

  return <Outlet />
}

// Admin layout — redirects non-admins to dashboard
function AdminLayout() {
  const { role } = useAuth()

  if (role !== 'admin' && role !== 'superadmin')
    return <Navigate to="/dashboard" />

  return <Outlet />
}

const noDemoRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: 'no-demo',
  component: NoDemoLayout,
})

const nonVolunteerRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: 'non-volunteer',
  component: NonVolunteerLayout,
})

const careReasonBatchRoute = createRoute({
  getParentRoute: () => nonVolunteerRoute,
  path: '/care-reasons/batch',
  component: CareReasonBatchFormPage,
})

const batchDetailRoute = createRoute({
  getParentRoute: () => nonVolunteerRoute,
  path: '/batches/$batchId',
  component: BatchDetailPage,
})

const adminRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: 'admin',
  component: AdminLayout,
})

const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/admin/users',
  component: UsersPage,
})

const adminIntegrationsRoute = createRoute({
  getParentRoute: () => noDemoRoute,
  path: '/admin/integrations',
  component: IntegrationsListPage,
})

const adminIntegrationNewRoute = createRoute({
  getParentRoute: () => noDemoRoute,
  path: '/admin/integrations/new',
  component: IntegrationFormPage,
})

const adminIntegrationEditRoute = createRoute({
  getParentRoute: () => noDemoRoute,
  path: '/admin/integrations/$integrationId/edit',
  component: IntegrationFormPage,
})

const adminBillingRoute = createRoute({
  getParentRoute: () => noDemoRoute,
  path: '/admin/billing',
  component: BillingPage,
})

// Platform layout — superadmin only, uses separate layout
function PlatformAuthenticatedLayout() {
  const { user, role, loading } = useAuth()

  if (loading) return <LoadingSpinner />
  if (!user) return <Navigate to="/login" />
  if (role !== 'superadmin') return <Navigate to="/dashboard" />

  return (
    <PlatformLayout>
      <Outlet />
    </PlatformLayout>
  )
}

const platformRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'platform',
  component: PlatformAuthenticatedLayout,
})

const platformOverviewRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform',
  component: PlatformOverviewPage,
})

const platformChurchesRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/churches',
  component: PlatformChurchesPage,
})

const platformChurchDetailRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/churches/$churchId',
  component: PlatformChurchDetailPage,
})

const platformUsersRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/users',
  component: PlatformUsersPage,
})

const platformAnalyticsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/analytics',
  component: PlatformAnalyticsPage,
})

const platformAuditLogsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: '/platform/audit-logs',
  component: PlatformAuditLogsPage,
})

// Share redirect routes — redirect to detail pages (fallback when Cloud Function isn't deployed)
const shareTaskRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/share/tasks/$taskId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/tasks/$taskId', params: { taskId: params.taskId } })
  },
})

const shareCareReasonRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/share/care-reasons/$reasonId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/care-reasons/$reasonId',
      params: { reasonId: params.reasonId },
    })
  },
})

const shareGroupRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/share/groups/$groupId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/groups/$groupId',
      params: { groupId: params.groupId },
    })
  },
})

const shareMemberRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/share/members/$memberId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/members/$memberId',
      params: { memberId: params.memberId },
    })
  },
})

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  component: SettingsPage,
})

const settingsProfileRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/profile',
  component: ProfileSettingsPage,
})

const settingsPasswordRoute = createRoute({
  getParentRoute: () => noDemoRoute,
  path: '/settings/password',
  component: PasswordSettingsPage,
})

const settingsNotificationsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings/notifications',
  component: NotificationSettingsPage,
})

const settingsTemplatesRoute = createRoute({
  getParentRoute: () => nonVolunteerRoute,
  path: '/settings/templates',
  component: TemplatesPage,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' })
  },
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  joinRoute,
  demoRoute,
  platformRoute.addChildren([
    platformOverviewRoute,
    platformChurchesRoute,
    platformChurchDetailRoute,
    platformUsersRoute,
    platformAnalyticsRoute,
    platformAuditLogsRoute,
  ]),
  authenticatedRoute.addChildren([
    // Accessible to all authenticated users including volunteers
    dashboardRoute,
    taskNewRoute,
    taskBatchRoute,
    taskDetailRoute,
    taskEditRoute,
    memberDetailRoute,
    settingsRoute,
      settingsProfileRoute,
      settingsNotificationsRoute,
    shareTaskRoute,
    shareMemberRoute,

    // Blocked for volunteers — redirects to /dashboard
    nonVolunteerRoute.addChildren([
      memberNewRoute,
      memberEditRoute,
      membersRoute,
      tasksRoute,
      careReasonNewRoute,
      careReasonDetailRoute,
      careReasonEditRoute,
      careReasonBatchRoute,
      batchDetailRoute,
      careReasonsRoute,
      groupNewRoute,
      groupDetailRoute,
      groupEditRoute,
      groupsRoute,
      goalNewRoute,
      goalDetailRoute,
      goalEditRoute,
      goalsRoute,
      reportsRoute,
      roadmapRoute,
      settingsTemplatesRoute,
      shareCareReasonRoute,
      shareGroupRoute,
    ]),
    adminRoute.addChildren([adminUsersRoute]),
    noDemoRoute.addChildren([
      adminIntegrationsRoute,
      adminIntegrationNewRoute,
      adminIntegrationEditRoute,
      adminBillingRoute,
      settingsPasswordRoute,
    ]),
  ]),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
